import { useEffect, useState } from 'react';
import ExitIntentPanel from "../components/ExitIntentPanel";
// import { Link } from 'react-router-dom';
import { ShoppingBag, BookOpen, Package, Star, Mail, Download, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../store/authStore';

import SEO from '../components/SEO';
import axios from 'axios';


const storeApi = axios.create({
  baseURL: (import.meta.env.VITE_API_URL || 'https://residualvault.com/api/v1').replace('/api/v1', '/api/store'),
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

interface Product {
  id: number;
  product_key: string;
  name: string;
  description: string;
  price_cents: number;
  is_bundle: boolean;
  includes_subscription: boolean;
  subscription_plan?: string;
  subscription_months?: number;
  preview_filename?: string;
}

export default function StorePage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [leadEmail, setLeadEmail] = useState('');
  const [leadName, setLeadName] = useState('');
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [previewKey, setPreviewKey] = useState('manual1');
  const { user, token } = useAuthStore();

  useEffect(() => {
    storeApi.get('/products')
      .then(res => setProducts(res.data.products))
      .catch(() => toast.error('Failed to load products'))
      .finally(() => setLoading(false));
  }, []);

  const handleCheckout = async (productKey: string) => {
    setCheckoutLoading(productKey);
    try {
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await storeApi.post('/checkout', {
        product_key: productKey,
        email: user?.email || undefined,
      }, { headers });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error('Failed to start checkout');
      setCheckoutLoading(null);
    }
  };

  const handleLeadCapture = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadEmail.includes('@')) { toast.error('Enter a valid email'); return; }
    try {
      await storeApi.post('/lead-capture', {
        email: leadEmail,
        first_name: leadName,
        manual_interest: previewKey,
        source: 'store_page',
      });
      setLeadSubmitted(true);
      toast.success('Preview ready! Check below.');
    } catch {
      toast.error('Something went wrong');
    }
  };

  const singles = products.filter(p => !p.is_bundle);
  const bundles = products.filter(p => p.is_bundle);

  const getProductIcon = (key: string) => {
    if (key === 'manual1') return <BookOpen className="w-8 h-8 text-sky-400" />;
    if (key === 'manual2') return <Star className="w-8 h-8 text-amber-400" />;
    if (key === 'manual_bundle') return <Package className="w-8 h-8 text-emerald-400" />;
    return <ShoppingBag className="w-8 h-8 text-purple-400" />;
  };

  const getRegularPrice = (key: string): string | null => {
    if (key === "manual1") return "$49.99";
    if (key === "manual2") return "$64.99";
    return null;
  };


  const getCoverImage = (key: string): string | null => {
    if (key === 'manual1') return '/covers/manual1-cover.jpg';
    if (key === 'manual2') return '/covers/manual2-cover.jpg';
    return null;
  };

  const getSavings = (p: Product) => {
    if (p.product_key === 'manual_bundle') return '$9.99';
    if (p.product_key === 'ultimate_bundle') return '$9.97';
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-400"></div>
      </div>
    );
  }

  return (
    <>
      <SEO title="Education Store — ResidualVault" description="Financial education manuals for passive income and crypto staking intelligence." canonical="https://residualvault.com/store" />
      <div className="min-h-screen">
        {/* Hero */}
        <section className="relative py-20 px-4 text-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-sky-900/20 to-transparent pointer-events-none" />
          <div className="relative max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              ResidualVault Education
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Master Your Money.<br />
              <span className="bg-gradient-to-r from-sky-400 to-emerald-400 bg-clip-text text-transparent">
                Build Passive Income.
              </span>
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
              Comprehensive financial education manuals by the ResidualVault Team. From budgeting fundamentals to advanced crypto staking — everything you need in one place.
            </p>
            <div className="flex items-center justify-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-1"><Download className="w-4 h-4" /> Instant PDF Download</span>
              <span className="flex items-center gap-1"><Shield className="w-4 h-4" /> 30-Day Access</span>
              <span className="flex items-center gap-1"><Mail className="w-4 h-4" /> Email Delivery</span>
            </div>
          </div>
        </section>

        {/* Individual Manuals */}
        <section className="max-w-6xl mx-auto px-4 pb-16">
          <h2 className="text-2xl font-bold text-white mb-8">Individual Manuals</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {singles.map(product => (
              <div key={product.id} className="bg-slate-800/50 border border-slate-700/50 rounded-2xl overflow-hidden hover:border-sky-500/30 transition-all group">
                {getCoverImage(product.product_key) && (
                  <div className="w-full flex justify-center bg-gradient-to-b from-slate-900 to-slate-800/50 py-6 px-8">
                    <img
                      src={getCoverImage(product.product_key)!}
                      alt={product.name + ' cover'}
                      className="w-48 h-auto rounded-lg shadow-2xl shadow-black/50 group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <div className="p-8">
                <div className="flex items-start justify-between mb-4">
                  {getProductIcon(product.product_key)}
                  <div className="text-right">
                    {getRegularPrice(product.product_key) && (
                      <div className="text-sm text-slate-400 mb-1">
                        Regular Price{" "}
                        <span style={{ textDecoration: "line-through", textDecorationColor: "#ef4444", textDecorationThickness: "2px" }} className="text-slate-400">{getRegularPrice(product.product_key)}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 justify-end">
                      {getRegularPrice(product.product_key) && <span className="text-sm text-emerald-400 font-medium">Sale Price</span>}
                      <span className="text-3xl font-bold text-white">${(product.price_cents / 100).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{product.name}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">{product.description}</p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleCheckout(product.product_key)}
                    disabled={checkoutLoading === product.product_key}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-sky-500/25 transition-all disabled:opacity-50"
                  >
                    {checkoutLoading === product.product_key ? 'Loading...' : 'Buy Now'}
                  </button>
                  {product.preview_filename && (
                    <a
                      href={`${(import.meta.env.VITE_API_URL || 'https://residualvault.com/api/v1').replace('/api/v1', '/api/store')}/preview/${product.product_key}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="py-3 px-4 border border-slate-600 text-slate-300 rounded-xl hover:border-sky-500/50 hover:text-sky-400 transition-all text-sm"
                    >
                      Preview
                    </a>
                  )}
                </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Bundles */}
        {bundles.length > 0 && (
          <section className="max-w-6xl mx-auto px-4 pb-16">
            <h2 className="text-2xl font-bold text-white mb-2">Bundle Deals</h2>
            <p className="text-slate-400 mb-8">Save more when you bundle</p>
            <div className="grid md:grid-cols-2 gap-8">
              {bundles.map(product => {
                const savings = getSavings(product);
                return (
                  <div key={product.id} className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-sky-500/30 rounded-2xl p-8 hover:border-sky-400/50 transition-all">
                    {savings && (
                      <div className="absolute -top-3 right-6 px-4 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full">
                        SAVE {savings}
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-4">
                      {getProductIcon(product.product_key)}
                      <span className="text-3xl font-bold text-white">${(product.price_cents / 100).toFixed(2)}</span>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{product.name}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed mb-4">{product.description}</p>
                    {product.includes_subscription && (
                      <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                        <Star className="w-4 h-4 text-purple-400" />
                        <span className="text-purple-300 text-sm">Includes {product.subscription_months}-month {product.subscription_plan} subscription</span>
                      </div>
                    )}
                    <button
                      onClick={() => handleCheckout(product.product_key)}
                      disabled={checkoutLoading === product.product_key}
                      className="w-full py-3 px-6 bg-gradient-to-r from-emerald-500 to-sky-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-emerald-500/25 transition-all disabled:opacity-50"
                    >
                      {checkoutLoading === product.product_key ? 'Loading...' : 'Get the Bundle'}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Free Preview / Lead Capture */}
        <section className="max-w-2xl mx-auto px-4 pb-20">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8 text-center">
            <BookOpen className="w-10 h-10 text-sky-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Not sure yet? Read a free preview.</h2>
            <p className="text-slate-400 mb-6 text-sm">Enter your email to unlock a free chapter preview of either manual.</p>
            {leadSubmitted ? (
              <div className="space-y-4">
                <p className="text-emerald-400 font-medium">Your preview is ready!</p>
                <a
                  href={`${(import.meta.env.VITE_API_URL || 'https://residualvault.com/api/v1').replace('/api/v1', '/api/store')}/preview/${previewKey}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 py-3 px-6 bg-sky-500 text-white rounded-xl font-semibold hover:bg-sky-400 transition-all"
                >
                  <Download className="w-4 h-4" /> Open Preview PDF
                </a>
              </div>
            ) : (
              <form onSubmit={handleLeadCapture} className="space-y-4 max-w-sm mx-auto">
                <input
                  type="text"
                  placeholder="First name (optional)"
                  value={leadName}
                  onChange={e => setLeadName(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:border-sky-500 focus:outline-none"
                />
                <input
                  type="email"
                  placeholder="Your email"
                  value={leadEmail}
                  onChange={e => setLeadEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:border-sky-500 focus:outline-none"
                />
                <select
                  value={previewKey}
                  onChange={e => setPreviewKey(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white focus:border-sky-500 focus:outline-none"
                >
                  <option value="manual1">The Passive Income Blueprint</option>
                  <option value="manual2">The Crypto Staking Intelligence Guide</option>
                </select>
                <button type="submit" className="w-full py-3 px-6 bg-sky-500 text-white rounded-xl font-semibold hover:bg-sky-400 transition-all">
                  Get Free Preview
                </button>
              </form>
            )}
          </div>
        </section>
      </div>
      <ExitIntentPanel />
    </>
  );
}
